import { checkCustomerLevelPTPAmount } from "@portal/api/ptp-list";
import { FormRules } from "@portal/BaseComponents/Form/type";
import { CustomerLevelPTPAmountCheckResult } from "@portal/types/entities/ptp";
import { formatCurrency } from "@portal/util/number-formatter";
import { debounce } from "lodash";
import { useEffect, useMemo, useRef } from "react";

/** Matches the typing-driven debounce used by the invoice search. */
export const CUSTOMER_LEVEL_AMOUNT_DEBOUNCE = 300;

interface CustomerLevelAmountValidatorProps {
  enabled: boolean;
  payDate?: string;
  currency?: string;
}

/**
 * Async rule for the customer-level amount: a promise against the customer as a whole cannot push
 * their open customer-level promises past their outstanding. Only the server can answer that, so
 * the rule asks it after typing settles and puts the verdict under the input instead of letting
 * the form fail on submit.
 *
 * Antd starts a validation per keystroke and waits on every promise it starts, so a superseded run
 * cannot simply be dropped - it has to settle. Each waiter is told whether it is the latest: the
 * last one proceeds to check, the rest pass, since the run behind them gives the real verdict.
 *
 * Advisory, not a reservation: the server rejects an over-promise on submit either way. For the
 * same reason a failed lookup passes rather than blocks.
 */
export default function useCustomerLevelAmountValidator(
  props: CustomerLevelAmountValidatorProps
): FormRules[number] {
  const { enabled, payDate, currency } = props;

  const waiting = useRef<Array<(isLatest: boolean) => void>>([]);

  const release = useMemo(
    () =>
      debounce(() => {
        const pending = waiting.current;
        waiting.current = [];
        pending.forEach((resolve, index) =>
          resolve(index === pending.length - 1)
        );
      }, CUSTOMER_LEVEL_AMOUNT_DEBOUNCE),
    []
  );

  useEffect(() => {
    return () => {
      release.cancel();
      waiting.current.forEach((resolve) => resolve(false));
      waiting.current = [];
    };
  }, [release]);

  function typingSettled() {
    return new Promise<boolean>((resolve) => {
      waiting.current.push(resolve);
      release();
    });
  }

  return {
    validator: async (_, value) => {
      if (!enabled || value === null || value === undefined || value <= 0) {
        return Promise.resolve();
      }

      if (!(await typingSettled())) {
        return Promise.resolve();
      }

      let check: CustomerLevelPTPAmountCheckResult;
      try {
        check = await checkCustomerLevelPTPAmount({
          amount: Number(value),
          pay_date: payDate,
        });
      } catch {
        return Promise.resolve();
      }

      if (check.valid) {
        return Promise.resolve();
      }

      const available = formatCurrency(check.available_amount, {
        currency: check.currency ?? currency ?? "USD",
      });
      return Promise.reject(
        new Error(
          `Cannot exceed ${available}. The rest of your outstanding is already promised`
        )
      );
    },
  };
}
